import get from 'lodash/get';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { fetchInfluncerMetrics } from 'modules/influencers/reducers/influencersMetrics';
import { fetchOfferDeliverables } from '../../subcampaigns/reducers/offerDeliverables';

import { createReducer } from 'utils/redux';
import { API, get as getRequest, patch } from 'utils/api';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';
import { __t, applyTranslations } from 'utils/language';
import { DELIVERABLE_POSTPONE_ACTION } from 'utils/deliverables';
import { isApplicableDeliverableType } from 'utils/bulk-offer-edit';

applyTranslations(require('modules/subcampaigns/components/bulk/bulk-edit-offer-modal/bulk-edit-offer-modal.i18n.json'));

const DESELECT_ALL = 'campaignDeliverablesByInfluencer/DESELECT_ALL';
const SELECT_ALL = 'campaignDeliverablesByInfluencer/SELECT_ALL';
const RECEIVE_IDS = 'campaignDeliverablesByInfluencer/RECEIVE_IDS';
const RECEIVE = 'campaignDeliverablesByInfluencer/RECEIVE';
const RESET = 'campaignDeliverablesByInfluencer/RESET';
const LOADING_STATE_CHANGED = 'campaignDeliverablesByInfluencer/LOADING_STATE_CHANGED';
const TOGGLE_DELIVERABLE_SELECTION = 'campaignDeliverablesByInfluencer/TOGGLE_DELIVERABLE_SELECTION';
const TOGGLE_DELIVERABLES_BY_TYPE = 'campaignDeliverablesByInfluencer/TOGGLE_DELIVERABLES_BY_TYPE';
const TOGGLE_CREATOR_SELECTION = 'campaignDeliverablesByInfluencer/TOGGLE_CREATOR_SELECTION';

const { CAMPAIGNS } = API.END_POINTS;

const CONTENT_CREATORS_PER_PAGE = 50;

const initialState = {
  groupedDeliverables: [],
  groupedDeliverablesIds: {},
  pagination: {},
  isLoading: false,
  selectedDeliverables: {}
};

const BULK_EDIT_MSG_KEY = 'bulk_edit_offer_modal';

export function loadingStateChanged(isLoading) {
  return {
    type: LOADING_STATE_CHANGED,
    payload: {
      isLoading
    }
  };
}

export function resetDeliverables() {
  return {
    type: RESET
  };
}

export function receiveDeliverables(groupedDeliverables, pagination) {
  return {
    type: RECEIVE,
    payload: {
      groupedDeliverables,
      pagination
    }
  };
}

export function receiveDeliverablesIds(groupedDeliverablesIds) {
  return {
    type: RECEIVE_IDS,
    payload: {
      groupedDeliverablesIds
    }
  };
}

export function fetchCampaignDeliverablesIdsByInfluencer(campaignId, options = {}) {
  return (dispatch) => {
    const queryString = Querystring.objectToString(snakeCaseKeys({
      search: options.search || ''
    }));

    const states = options.states && options.states.join(',') || '';
    const url = `${CAMPAIGNS.getCampaignGroupedDeliverablesIds(campaignId)}?${queryString}&states=${states}`;

    if (!states) {
      return dispatch(receiveDeliverablesIds([]));
    }

    dispatch(loadingStateChanged(true));

    return getRequest(url)
      .then(response => {
        dispatch(receiveDeliverablesIds(response.data));
      })
      .catch((error) => {
        dispatch(receiveDeliverablesIds([]));

        dispatch(loadingStateChanged(false));
        throw error;
      });
  };
}

export function fetchDeliverablesByInfluencer(campaignId, options = {}) {
  return (dispatch) => {
    const params = {
      search: options.search || '',
      page: options.pageId || 1,
      sort_by: options.sortingKey ?? undefined,
      order: `${options.ascending ? 'asc' : 'desc'}` ?? undefined,
      itemsPerPage: CONTENT_CREATORS_PER_PAGE
    };

    const queryString = Querystring.objectToString(snakeCaseKeys(params));
    const states = options.states && options.states.join(',') || '';
    const url = `${CAMPAIGNS.getCampaignGroupedDeliverables(campaignId)}?${queryString}&states=${states}`;

    if (!states) {
      return dispatch(receiveDeliverables([], {
        links: {}
      }));
    }

    dispatch(loadingStateChanged(true));

    return getRequest(url, {}, { suppressGlobalNotification: true })
      .then(response => {
        const groupedDeliverables = response.data.data.map(group => ({
          ...group,
          ...group.creatorProfile
        }));

        dispatch(receiveDeliverables(groupedDeliverables, {
          ...response.data.meta,
          links: response.data.links
        }));

        return groupedDeliverables;
      })
      .then(groupedDeliverables => {
        const influencerIds = groupedDeliverables.map(group => group.creatorProfile.id);

        dispatch(fetchInfluncerMetrics(undefined, influencerIds, true));
        dispatch(loadingStateChanged(false));
      })
      .catch((error) => {
        dispatch(receiveDeliverables([], {
          links: {}
        }));

        dispatch(loadingStateChanged(false));
        throw error;
      });
  };
}

export function deselectAllDeliverables() {
  return {
    type: DESELECT_ALL
  };
}

export function selectAllDeliverables() {
  return {
    type: SELECT_ALL
  };
}

export function toggleDeliverableSelection(creatorId, deliverableId) {
  return {
    type: TOGGLE_DELIVERABLE_SELECTION,
    payload: {
      creatorId,
      deliverableId
    }
  };
}

export function toggleDeliverableByType(deliverablesMap) {
  return {
    type: TOGGLE_DELIVERABLES_BY_TYPE,
    payload: {
      deliverablesMap
    }
  };
}

export function toggleCreatorSelection(creatorId, isChecked) {
  return {
    type: TOGGLE_CREATOR_SELECTION,
    payload: {
      creatorId,
      isChecked
    }
  };
}

function getSelectedDeliverables(selectedDeliverables, groupedDeliverablesIds, actionType) {
  const isEnableFiltering = actionType !== DELIVERABLE_POSTPONE_ACTION;
  const deliverablesIds = [];

  const groupedDeliverablesSet = Object.values(groupedDeliverablesIds)
    .flatMap(ids => ids
      .map(id => String(id))
    );

  Object.values(selectedDeliverables).forEach(deliverables => {
    Object.keys(deliverables).reduce((res, id) => {
      if (!groupedDeliverablesSet.includes(String(id))) {
        return res;
      }

      const deliverable = deliverables[id];

      if (!isEnableFiltering) {
        if (deliverable.isChecked) {
          res.push(id);
        }

        return res;
      }

      if (deliverable.isChecked && isApplicableDeliverableType(deliverable.type, actionType)) {
        res.push(id);
      }

      return res;
    }, deliverablesIds);
  });

  return deliverablesIds;
}

export function updateOffers(campaignId, action, options) {
  return (dispatch, getState) => {
    const url = CAMPAIGNS.editBulkOffers(campaignId);

    const offerId = get(getState().subcampaign.data, 'offer.id');
    const { selectedDeliverables, groupedDeliverablesIds } = getState().campaignDeliverablesByInfluencer;
    const deliverablesIds = getSelectedDeliverables(selectedDeliverables, groupedDeliverablesIds, action);

    dispatch(showLoading(url));

    const data = {
      action,
      deliverablesIds,
      ...options
    };

    return patch(url, { data })
      .then(() => { // eslint-disable-line consistent-return
        dispatch(hideLoading(url));
        if (offerId) {
          dispatch(fetchOfferDeliverables(offerId, true));
        }
        dispatch(success(__t(`${BULK_EDIT_MSG_KEY}.label_success`)));
      })
      .catch((error) => {
        const errorMsgByCode = __t(`${BULK_EDIT_MSG_KEY}.errors.${error?.response?.data?.code}`);
        const errorMsg = errorMsgByCode || __t(`${BULK_EDIT_MSG_KEY}.errors.system`);

        dispatch(danger(errorMsg));
        dispatch(hideLoading(url));

        throw error;
      });
  };
}

export default createReducer('campaignDeliverablesByInfluencer', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RECEIVE]: (state, { groupedDeliverables, pagination }) => ({
    ...state,
    groupedDeliverables,
    pagination,
    selectedDeliverables: {
      ...state.selectedDeliverables,
      ...groupedDeliverables.reduce((selectedDeliverables, group) => {
        // eslint-disable-next-line no-param-reassign
        selectedDeliverables[group.creatorProfile.id] = {
          ...group.deliverables.reduce((res, deliverable) => {
            res[deliverable.id] = {
              id: deliverable.id,
              type: deliverable.deliverableType,
              isChecked: false
            };

            if (state.selectedDeliverables[group.creatorProfile.id] && state.selectedDeliverables[group.creatorProfile.id][deliverable.id]) {
              res[deliverable.id].isChecked = state.selectedDeliverables[group.creatorProfile.id][deliverable.id].isChecked;
            }

            return res;
          }, {}),
          ...state.selectedDeliverables[group.creatorProfile.id]
        };
        return selectedDeliverables;
      }, {})
    }
  }),
  [TOGGLE_CREATOR_SELECTION]: (state, { creatorId, isChecked }) => ({
    ...state,
    selectedDeliverables: {
      ...state.selectedDeliverables,
      [creatorId]: Object.values(state.selectedDeliverables[creatorId]).reduce((res, deliverable) => {
        res[deliverable.id] = {
          ...state.selectedDeliverables[creatorId][deliverable.id],
          isChecked
        };
        return res;
      }, {})
    }
  }),
  [TOGGLE_DELIVERABLE_SELECTION]: (state, { creatorId, deliverableId }) => ({
    ...state,
    selectedDeliverables: {
      ...state.selectedDeliverables,
      [creatorId]: {
        ...state.selectedDeliverables[creatorId],
        [deliverableId]: {
          ...state.selectedDeliverables[creatorId][deliverableId],
          isChecked: !state.selectedDeliverables[creatorId][deliverableId].isChecked
        }
      }
    }
  }),
  [TOGGLE_DELIVERABLES_BY_TYPE]: (state, { deliverablesMap }) => {
    const newState = {};

    Object.keys(deliverablesMap).forEach(key => {
      const creatorId = Number(key);
      const deliverableStatuses = deliverablesMap[creatorId];
      const deliverables = {};

      deliverableStatuses.forEach(deliverableStatusMap => {
        const deliverableId = Number(Object.keys(deliverableStatusMap)[0]);

        deliverables[deliverableId] = {
          ...state.selectedDeliverables[creatorId][deliverableId],
          isChecked: deliverableStatusMap[deliverableId]
        };
      });

      newState[creatorId] = {
        ...state.selectedDeliverables[creatorId],
        ...deliverables
      };
    });

    return {
      ...state,
      selectedDeliverables: {
        ...state.selectedDeliverables,
        ...newState
      }
    };
  },
  [LOADING_STATE_CHANGED]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [RECEIVE_IDS]: (state, { groupedDeliverablesIds }) => ({
    ...state,
    groupedDeliverablesIds: groupedDeliverablesIds.reduce((res, item) => {
      res[item.creatorProfileId] = item.deliverableIds;
      return res;
    }, {})
  }),
  [SELECT_ALL]: (state) => ({
    ...state,
    selectedDeliverables: {
      ...state.selectedDeliverables,
      ...Object.keys(state.groupedDeliverablesIds).reduce((selectedDeliverables, creatorId) => {
        // eslint-disable-next-line no-param-reassign
        selectedDeliverables[creatorId] = {
          ...state.selectedDeliverables[creatorId],
          ...Object.values(state.groupedDeliverablesIds[creatorId]).reduce((deliverables, deliverableId) => {
            // eslint-disable-next-line no-param-reassign
            deliverables[deliverableId] = {};

            if (state.selectedDeliverables[creatorId] && state.selectedDeliverables[creatorId][deliverableId]) {
              // eslint-disable-next-line no-param-reassign
              deliverables[deliverableId] = {
                ...state.selectedDeliverables[creatorId][deliverableId]
              };
            }

            // eslint-disable-next-line no-param-reassign
            deliverables[deliverableId].isChecked = true;

            return deliverables;
          }, {})
        };

        return selectedDeliverables;
      }, {})
    }
  }),
  [DESELECT_ALL]: (state) => ({
    ...state,
    selectedDeliverables: {
      ...state.selectedDeliverables,
      ...Object.keys(state.groupedDeliverablesIds).reduce((selectedDeliverables, creatorId) => {
        // eslint-disable-next-line no-param-reassign
        selectedDeliverables[creatorId] = {
          ...state.selectedDeliverables[creatorId],
          ...Object.values(state.groupedDeliverablesIds[creatorId]).reduce((deliverables, deliverableId) => {
            // eslint-disable-next-line no-param-reassign
            deliverables[deliverableId] = {
              ...state.selectedDeliverables[creatorId][deliverableId],
              isChecked: false
            };

            return deliverables;
          }, {})
        };

        return selectedDeliverables;
      }, {})
    }
  })
});
